// HERO-KÁRTYA — a Pénzügy lap FELSŐ, tónusos összegző kártyáinak EGY közös alakja.
//
// ══ MIÉRT KÖZÖS RÉTEG ═══════════════════════════════════════════════════════════════════════════════════
// Öt példány él belőle, KÉT feature-ben: Bevétel · Kiadás · Egyenleg (`features/finance`) és Adott havi ·
// Várható következő havi (`features/revenue-forecast`). Mindegyik külön volt megírva, és a DRY-szabály
// második előfordulásnál már a közös rétegbe rendeli — a szórás pedig itt MÉRHETŐ volt: a három összkép-kártya
// a számot a leírás ALÁ tette, a „következő havi" a sor JOBB szélére, az „adott havi" pedig egy harmadik
// alakba (címke-érték párok). Egymás alatt állva a lap három különböző kártya-nyelvet beszélt.
//
// ══ MIÉRT A SZÁM A CÍM MELLETT, ÉS NEM ALATTA ═══════════════════════════════════════════════════════════
// A kártya alatta-alakban 1920 px-en ~530 px széles volt, a szám pedig ~185 px — a maradék ~345 px ÜRES
// (user-lelet: „vízszintesen rohadt nagy gap-ek"). A szám a sor jobb szélén ezt a helyet tölti ki, a kártya
// két sorral alacsonyabb lesz, és a három/öt kártya EGY vonalban olvasható végig. Szűk sávon (`flex-wrap`)
// a szám a következő sorba csúszik — ott a hely tényleg kevés, nem üres.
import { type ReactNode } from 'react';

import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';

/** A kártya TÓNUSA = a szám jelentése: bejövő pénz (success) · kimenő (danger) · összegző/előretekintő (brand). */
export type HeroStatTone = 'success' | 'danger' | 'brand';

const TONE_CARD: Record<HeroStatTone, string> = {
  success: 'border-success/40 from-success/10 to-success/5',
  danger: 'border-danger-500/40 from-danger-500/10 to-danger-500/5',
  brand: 'border-brand/40 from-brand/10 to-brand/5',
};
const TONE_ICON: Record<HeroStatTone, string> = {
  success: 'bg-success/15 text-success',
  danger: 'bg-danger-soft text-danger-600',
  brand: 'bg-brand/15 text-brand',
};

export interface HeroStatCardProps {
  tone: HeroStatTone;
  icon: ReactNode;
  title: string;
  description: string;
  /**
   * A KÉSZRE FORMÁZOTT összeg — a kártya nem formáz, a hívó adja (a pénz Decimal-stringként érkezik, és a
   * formázás a hívó locale-ját ismeri).
   */
  value?: string;
  /**
   * A `value` HELYETT álló, összetettebb érték-blokk (pl. terv–tény pár a saját címkéivel). Pontosan az
   * egyik adandó meg: `value` VAGY `valueSlot`.
   */
  valueSlot?: ReactNode;
}

export function HeroStatCard({
  tone,
  icon,
  title,
  description,
  value,
  valueSlot,
}: HeroStatCardProps): JSX.Element {
  return (
    <div
      className={cn(
        'card-glass flex flex-wrap items-center justify-between gap-x-6 gap-y-3 rounded-xl bg-gradient-to-br p-4',
        TONE_CARD[tone],
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        <span
          className={cn(
            'flex size-10 shrink-0 items-center justify-center rounded-xl',
            TONE_ICON[tone],
          )}
        >
          {icon}
        </span>
        <div className="min-w-0">
          <Text variant="itemTitle" as="div">
            {title}
          </Text>
          <Text variant="secondary" as="div" className="text-xs">
            {description}
          </Text>
        </div>
      </div>
      {/* `ml-auto`: ha a sor TÖRDEL (szűk sáv), az érték akkor is a JOBB szélen marad — különben a
          `justify-between` a új sor ELEJÉRE tenné, és a kártya jobb fele üresen maradna. */}
      <div className="ml-auto">
        {valueSlot ?? (
          <span className="text-foreground text-2xl font-semibold tabular-nums tracking-tight">
            {value}
          </span>
        )}
      </div>
    </div>
  );
}
